import { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider } from './contexts/AuthContext';
import { useSEO } from './hooks/useSEO';
import Header from './components/Header';
import Editor from './components/Editor';
import Viewer from './components/Viewer';
import PublishedViewer from './components/PublishedViewer';
import Login from './components/Login';
import Register from './components/Register';
import ForgotPassword from './components/ForgotPassword';
import Dashboard from './components/Dashboard';
import URLOpener from './components/URLOpener';
import LoadingPage from './components/LoadingPage';
import './components/URLOpener.css';
import './components/LoadingPage.css';
import './App.css';

// Layout wrapper that conditionally shows header/footer
function Layout({ children, theme, toggleTheme }) {
  const location = useLocation();
  const hideLayout = ['/login', '/register', '/dashboard', '/forgot-password', '/url-opener'].includes(location.pathname);
  
  // Initialize SEO for each route
  useSEO();

  if (hideLayout) {
    return <>{children}</>;
  }

  return (
    <div className="app" data-theme={theme}>
      <Header theme={theme} toggleTheme={toggleTheme} />
      <main className="main-content">
        {children}
      </main>
      <footer className="footer">
        <p>Scrawl — Share code and text snippets instantly</p>
        <span className="footer-credit">Built by <a href="https://github.com/Manas-xt" target="_blank" rel="noopener noreferrer">Manas</a> ⚡</span>
      </footer>
    </div>
  );
}

function AppRoutes({ theme, toggleTheme }) {
  return (
    <Layout theme={theme} toggleTheme={toggleTheme}>
      <Routes>
        <Route path="/" element={<Editor theme={theme} />} />
        <Route path="/view/:data" element={<Viewer theme={theme} />} />
        <Route path="/p/:shortId" element={<PublishedViewer theme={theme} />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/url-opener" element={<URLOpener />} />
      </Routes>
    </Layout>
  );
}

function App() {
  const [theme, setTheme] = useState(() => {
    const saved = localStorage.getItem('scrawl-theme');
    return saved || 'dark';
  });
  
  // Only show loading on first visit (per session)
  const [isLoading, setIsLoading] = useState(() => {
    return !sessionStorage.getItem('scrawl-loaded');
  });

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('scrawl-theme', theme);
  }, [theme]);

  // Show loading only on first visit
  useEffect(() => {
    if (isLoading) {
      const timer = setTimeout(() => {
        setIsLoading(false);
        sessionStorage.setItem('scrawl-loaded', 'true');
      }, 1500);
      
      return () => clearTimeout(timer);
    }
  }, [isLoading]);

  const toggleTheme = () => {
    setTheme(prev => prev === 'dark' ? 'light' : 'dark');
  };

  if (isLoading) {
    return <LoadingPage />;
  }

  return (
    <AuthProvider>
      <Router>
        <Toaster 
          position="bottom-right"
          toastOptions={{
            duration: 3000,
            style: {
              background: theme === 'dark' ? '#1e1e2e' : '#ffffff',
              color: theme === 'dark' ? '#cdd6f4' : '#1e1e2e',
              border: `1px solid ${theme === 'dark' ? '#313244' : '#e0e0e0'}`,
            },
          }}
        />
        <AppRoutes theme={theme} toggleTheme={toggleTheme} />
      </Router>
    </AuthProvider>
  );
}

export default App;
